class VideoPlayer extends HTMLElement {
  #controller = new AbortController();

  constructor() {
    super();

    this.#injectButton();
  }

  get video() {
    const video = this.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return null;
    return video;
  }

  get button() {
    const button = this.querySelector('button');
    if (!(button instanceof HTMLButtonElement)) return null;
    return button;
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  connectedCallback() {
    const { video, button } = this;
    if (!video || !button) return;

    const { signal } = this.#controller;

    this.#updateButtonState(this.autoplay);

    if (this.autoplay) {
      video.muted = true;
      
      // Safari-specific: Ensure video is loaded before attempting to play
      const attemptAutoplay = () => {
        const playPromise = video.play();
        
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            this.#updateButtonState(false);
            
            // Try again on first user interaction
            const playOnInteraction = () => {
              video.play().catch(() => {});
              document.removeEventListener('click', playOnInteraction);
              document.removeEventListener('touchstart', playOnInteraction);
            };
            
            document.addEventListener('click', playOnInteraction, { once: true });
            document.addEventListener('touchstart', playOnInteraction, { once: true });
          });
        }
      };
      
      // Check if video is ready to play
      if (video.readyState >= 3) {
        attemptAutoplay();
      } else {
        video.addEventListener('canplaythrough', attemptAutoplay, { once: true, signal });
        video.addEventListener('loadeddata', () => {
          if (video.paused && this.autoplay) {
            attemptAutoplay();
          }
        }, { once: true, signal });
      }
    }

    button.addEventListener('click', this.#handleClick, { signal });
    video.addEventListener('play', () => this.#updateButtonState(true), { signal });
    video.addEventListener('pause', () => this.#updateButtonState(false), { signal });

  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { video } = this;
    if (!video) return;

    if (video.paused) {
      video.play().catch(() => { });
    } else {
      video.pause();
    }
  };

  /** @param {boolean} isPlaying */
  #updateButtonState(isPlaying) {
    const { button } = this;
    if (!button) return;

    button.setAttribute('data-playing', String(isPlaying));

    const ariaLabel = isPlaying ? Theme.translations.pause_video : Theme.translations.play_video;
    button.setAttribute('aria-label', ariaLabel || '');
  }

  #injectButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.classList.add('video-player__toggle');
    button.setAttribute('aria-label', Theme.translations.play_video || '');

    button.innerHTML = `
      <img src="${Theme.icons.play}" alt="${Theme.translations.play_video}" class="icon-play">
      <img src="${Theme.icons.pause}" alt="${Theme.translations.pause_video}" class="icon-pause">
    `;

    this.appendChild(button);
  }
}

if (!customElements.get('video-player')) {
  customElements.define('video-player', VideoPlayer);
}

